import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { wrapCreateBrowserRouter } from '@sentry/react';
import { ConsentPage } from '@/features/consent';
import LoginPage from '@/pages/auth';
import OidcSskCompleteSigningPage from '@/pages/oidc-ssk/complete-signing-page';
import CompleteSigningPage from '@/pages/saml-ssk/complete-signing-page';
import AuthMfaFactorPage from '../pages/auth/mfa-factor';
import AccessDeniedByPolicyPage from '../pages/error/access-denied-by-policy-page';
import AuthErrorPage from '../pages/error/auth-error-page';
import NotFoundPage from '../pages/error/not-found-page';
import RequestErrorPage from '../pages/error/request-error-page';
import UnauthorizedPage from '../pages/error/unauthorized-page';
import InvitationRouter from '../pages/invitation/invitation-router';
import { RootLayout } from './root-layout';
import { SignOnRoutes } from './sign-on-routes';

// Dev-only screen gallery for design review. The dynamic import lives inside
// this DEV-guarded branch, so production builds evaluate import.meta.env.DEV to
// false, drop the route, and strip the showcase code from the bundle entirely.
const showcaseRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: 'showcase',
        lazy: async () => {
          const { default: Component } =
            await import('@/pages/showcase/showcase-page');

          return { Component };
        },
      },
    ]
  : [];

// Wrapped so Sentry names navigations by route pattern, keeping tenant paths
// and pairing codes out of transaction names.
const createRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const router = createRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: [
      // All routes under /idp for unified tenant routing
      {
        path: 'idp',
        children: [
          {
            element: <SignOnRoutes />,
            children: [
              {
                index: true,
                element: <LoginPage />,
              },
              {
                path: 'login',
                element: <LoginPage />,
              },
              {
                path: 'mfa-factor',
                element: <AuthMfaFactorPage />,
              },
              {
                path: 'consent',
                element: <ConsentPage />,
              },
            ],
          },
          {
            path: 'unauthorized',
            element: <UnauthorizedPage />,
          },
          {
            path: 'access-denied-by-policy',
            element: <AccessDeniedByPolicyPage />,
          },
          {
            path: 'auth-error',
            element: <AuthErrorPage />,
          },
          {
            path: 'request-error',
            element: <RequestErrorPage />,
          },
          {
            path: 'saml-ssk/complete-signing',
            element: <CompleteSigningPage />,
          },
          {
            path: 'oidc-ssk/complete-signing',
            element: <OidcSskCompleteSigningPage />,
          },
          {
            path: 'invitation/:pairingCode',
            element: <InvitationRouter />,
          },
          ...showcaseRoutes,
          // Auth routes (kept for backward compatibility)
          {
            path: 'auth',
            element: <SignOnRoutes />,
            children: [
              {
                index: true,
                element: <LoginPage />,
              },
              {
                path: 'mfa-factor',
                element: <AuthMfaFactorPage />,
              },
            ],
          },
        ],
      },
    ],
  },
]);
